import React, { useState, useEffect, useCallback } from 'react';
import DailyForecastControl from '../../ControlPanel/DailyForecastControl';
import DailyForecastApexChart from '../../Charts/DailyForecastApexChart';
import KPICards from '../../Charts/KPICards';
import NPIBadge from '../../NPIBadge/NPIBadge';
import VersionSelector from '../../VersionSelector';
import { formatNum, pct } from '../../../utils_1/formatters';
import { apiGet, apiPost } from '../../../services_1/apiClient';
import HelpTooltip from '../../HelpTooltip/HelpTooltip';
import { TOOLTIP_CONTENT } from '../../../constants/tooltipContent';
import { useAuth } from '../../../context/AuthContext';
import './DashboardWindow.css';

const DashboardWindow = ({ onDailyForecast, loading, dataSummary, dailyForecastData }) => {
  const { user } = useAuth();
  const isExecutive = user?.role === 'executive';
  const isAdmin = user?.role === 'admin';
  const canSeeKPIs = true;
  const [globalKPIs, setGlobalKPIs] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [kpiDetails, setKpiDetails] = useState(null);

  // Track selected SKU for NPI check
  const [selectedSkuId, setSelectedSkuId] = useState('');

  // Save forecast state (keeps the forecast log for history and audit)
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { type: 'success'|'error', text }

  // Baseline overlay state (model-derived line on the main chart)
  const [showBaseline, setShowBaseline] = useState(false);
  const [baselineData, setBaselineData] = useState(null);   // Hybrid baseline values array
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineInfo, setBaselineInfo] = useState(null);   // { window, historical_avg, mape, method }

  // Versioning state
  const [showVersions, setShowVersions] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [versionMsg, setVersionMsg] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  // Keep last params so Save Forecast can replay with same filters
  const [lastParams, setLastParams] = useState(null);

  // ── Derived data ─────────────────────────────────────────────────────
  const forecastItem = dailyForecastData?.forecasts?.[0] || null;
  const forecastScope = dailyForecastData?.parameters_used ?? null;

  const describeScope = useCallback((scopeParams) => {
    if (!scopeParams) return 'All data';
    const bits = [];
    if (scopeParams.brand) bits.push(`Brand ${scopeParams.brand}`);
    if (scopeParams.sku_id) bits.push(`SKU ${scopeParams.sku_id}`);
    if (scopeParams.channel) bits.push(`Channel ${scopeParams.channel}`);
    if (scopeParams.location) bits.push(`Location ${scopeParams.location}`);
    if (scopeParams.region) bits.push(`Region ${scopeParams.region}`);
    if (scopeParams.start_date && scopeParams.end_date) bits.push(`${scopeParams.start_date} to ${scopeParams.end_date}`);
    return bits.length > 0 ? bits.join(' | ') : 'All data';
  }, []);

  // ── Fetch Enterprise KPIs on mount + manual refresh ─────────────────
  const fetchKPIs = useCallback(async (scopeParams = null) => {
    setKpiLoading(true);
    try {
      const query = new URLSearchParams();
      const params = scopeParams || {};
      if (params.sku_id) query.set('sku_id', params.sku_id);
      if (params.brand) query.set('brand', params.brand);
      if (params.channel) query.set('channel', params.channel);
      if (params.location) query.set('location', params.location);
      if (params.region) query.set('region', params.region);
      if (params.start_date) query.set('start_date', params.start_date);
      if (params.end_date) query.set('end_date', params.end_date);
      if (params.forecast_granularity) query.set('granularity', params.forecast_granularity);
      const forecastDays = Number(params.forecast_days);
      const kpiDaysBack = Number.isFinite(params.kpi_days_back)
        ? params.kpi_days_back
        : (Number.isFinite(forecastDays) && forecastDays > 0 ? forecastDays : 90);
      query.set('days_back', String(kpiDaysBack));

      const queryString = query.toString();
      const data = await apiGet(`/api/kpis${queryString ? `?${queryString}` : ''}`);
      const isHybridMetrics = data.metrics_source === 'hybrid_holdout';
      const scopeLabel = data.scope_label || describeScope(params);
      const selectedScope = scopeLabel && scopeLabel !== 'All data';
      setKpiDetails(data.performance_metrics || null);
      setGlobalKPIs([
        {
          label: data.fa.label,
          value: data.fa.value != null ? `${data.fa.value.toFixed(1)}${data.fa.unit}` : 'N/A',
          sub: data.fa.value != null
            ? (selectedScope
              ? `Selected scope: ${scopeLabel}`
              : (isHybridMetrics ? 'Hybrid holdout test-set accuracy' : 'vs Actuals (30d)'))
            : (isHybridMetrics ? 'Awaiting hybrid model metrics' : 'Awaiting forecast history — click Save Forecast'),
          icon: '🎯',
          color: 'green',
          tooltip: TOOLTIP_CONTENT.forecast_accuracy,
          onClick: data.performance_metrics ? () => setKpiDetails(data.performance_metrics) : undefined,
        },
        {
          label: data.fb.label,
          value: data.fb.value != null
            ? `${data.fb.value > 0 ? '+' : ''}${data.fb.value.toFixed(1)}${data.fb.unit}`
            : 'N/A',
          sub: data.fb.value != null
            ? (isHybridMetrics ? 'Hybrid model bias' : 'Positive = Over-forecast')
            : (isHybridMetrics ? 'Awaiting hybrid model metrics' : 'Awaiting forecast history — click Save Forecast'),
          icon: '⚖️',
          color: data.fb.value != null && (data.fb.value > 10 || data.fb.value < -10) ? 'red' : 'blue',
          tooltip: TOOLTIP_CONTENT.forecast_bias,
        },
        {
          label: data.dio.label,
          value: data.dio.value != null ? `${Math.round(data.dio.value)} ${data.dio.unit}` : 'N/A',
          sub: data.dio.value != null ? 'Inventory health' : 'No inventory data',
          icon: '🏭',
          color: data.dio.value != null && data.dio.value > 30 ? 'orange' : 'teal',
          tooltip: TOOLTIP_CONTENT.dio,
        },
        {
          label: data.otif.label,
          value: data.otif.value != null ? `${data.otif.value.toFixed(1)}${data.otif.unit}` : 'N/A',
          sub: data.otif.value != null ? 'On-Time In-Full' : 'No delivery data',
          icon: '🚚',
          color: 'purple',
          tooltip: TOOLTIP_CONTENT.otif,
        },
        {
          label: data.fva?.label || 'Forecast Value Add',
          value: data.fva?.value != null
            ? `${data.fva.value > 0 ? '+' : ''}${data.fva.value.toFixed(1)}${data.fva.unit}`
            : 'N/A',
          sub: data.fva?.value != null
            ? (data.fva.value >= 0 ? '✅ Model beats baseline' : '⚠️ Baseline more accurate')
            : 'Generate a forecast to calculate',
          icon: '📏',
          color: data.fva?.value != null && data.fva.value >= 0 ? 'green' : 'orange',
          tooltip: TOOLTIP_CONTENT.fva,
        },
      ]);
    } catch (err) {
      console.error('Failed to load KPIs:', err);
      setGlobalKPIs([
        { label: 'Forecast Accuracy',          value: 'N/A', sub: 'Unable to load', icon: '🎯', color: 'green'  },
        { label: 'Forecast Bias',              value: 'N/A', sub: 'Unable to load', icon: '⚖️', color: 'blue'   },
        { label: 'Days Inventory Outstanding', value: 'N/A', sub: 'Unable to load', icon: '🏭', color: 'teal'   },
        { label: 'On-Time In-Full',            value: 'N/A', sub: 'Unable to load', icon: '🚚', color: 'purple' },
        { label: 'Forecast Value Add',         value: 'N/A', sub: 'Unable to load', icon: '📏', color: 'orange' },
      ]);
      setKpiDetails(null);
    } finally {
      setKpiLoading(false);
    }
  }, [describeScope]);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  // ── Intercept forecast call to capture params + selected SKU ────────
  const handleForecast = useCallback(async (params) => {
    setLastParams(params);
    setSelectedSkuId(params.sku_id || '');
    setSaveMsg(null);
    setVersionMsg(null);
    // Reset baseline when a new forecast is run
    setBaselineData(null);
    setBaselineInfo(null);
    setShowBaseline(false);
    try {
      const maybePromise = onDailyForecast(params);
      if (maybePromise && typeof maybePromise.then === 'function') {
        await maybePromise;
      }
      await fetchKPIs(params);
    } catch (err) {
      console.error('Forecast refresh failed:', err);
    }
  }, [fetchKPIs, onDailyForecast]);

  // ── Toggle baseline overlay ────────────────────────────────────────────
  const handleToggleBaseline = useCallback(async () => {
    if (showBaseline) {
      // Toggle off
      setShowBaseline(false);
      setBaselineData(null);
      setBaselineInfo(null);
      return;
    }
    if (!lastParams) return;
    setBaselineLoading(true);
    try {
      const resolvedSkuId = forecastScope?.resolved_sku_id || lastParams.sku_id || null;
      const resolvedChannel = forecastScope?.resolved_channel || lastParams.channel || null;
      const resolvedLocation = forecastScope?.resolved_location || lastParams.location || null;
      const resolvedStartDate = forecastScope?.actual_start_date_used
        || forecastItem?.dates?.[0]
        || lastParams.start_date
        || null;

      const data = await apiPost('/api/baseline-forecast', {
        sku_id:        resolvedSkuId,
        channel:       resolvedChannel,
        location:      resolvedLocation,
        brand:         lastParams.brand || null,
        start_date:    resolvedStartDate,
        forecast_days: lastParams.forecast_days || 30,
        method:        'sma',
        sma_window:    0,
      });
      if (data.sma && data.sma.values) {
        // Align baseline to forecast dates (slice/pad to same length)
        const forecastLen = forecastItem?.dates?.length || data.sma.dates.length;
        const aligned = data.sma.values.slice(0, forecastLen);
        setBaselineData(aligned);
        setBaselineInfo({
          window: data.sma.window,
          avg: data.sma.historical_avg,
          mape: data.sma.in_sample_mape,
          method: data.sma.method,
        });
        setShowBaseline(true);
      }
    } catch (e) {
      console.error('Baseline forecast failed:', e);
    } finally {
      setBaselineLoading(false);
    }
  }, [showBaseline, lastParams, forecastItem, forecastScope]);

  // ── Save Forecast → /api/daily-forecast/save ────────────────────────
  // Writes to forecast_log.csv for history, comparisons, and auditability
  const handleSaveForecast = async () => {
    if (!lastParams) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const d = await apiPost('/api/daily-forecast/save', lastParams);
      setSaveMsg({
        type: 'success',
        text: `✅ Saved ${d._log_rows_written || 0} forecast rows to the log.`,
      });
    } catch (e) {
      setSaveMsg({ type: 'error', text: `❌ Save failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  // ── Save Version → POST /api/forecast-versions ──────────────────────
  const handleSaveVersion = async () => {
    if (!dailyForecastData || !versionName.trim()) return;
    setSavingVersion(true);
    setVersionMsg(null);
    try {
      const fc = dailyForecastData.forecasts?.[0] || {};
      const payload = {
        name: versionName.trim(),
        description: `Dashboard forecast — ${new Date().toLocaleDateString()}`,
          forecast_data: {
            dates:          fc.dates || [],
            base_forecast:  fc.base_forecast || [],
            total_forecast: fc.total_forecast || [],
            confidence_upper: fc.confidence_upper || [],
            confidence_lower: fc.confidence_lower || [],
            sku_id:         fc.sku_id,
            method:         fc.forecasting_method,
          },
        metadata: {
          region: lastParams?.region || '',
          sku_id: lastParams?.sku_id || 'all',
        },
      };
      await apiPost('/api/forecast-versions', payload);
      setVersionMsg({
        type: 'success',
        text: `✅ Version "${versionName.trim()}" saved successfully.`,
      });
      setVersionName('');
    } catch (e) {
      setVersionMsg({ type: 'error', text: `❌ ${e.message}` });
    } finally {
      setSavingVersion(false);
    }
  };

  // ── Version comparison — calls backend + stores result ──────────────
  const handleVersionCompare = useCallback(async (selectedIds) => {
    if (selectedIds.length < 2) {
      setCompareResult(null);
      return;
    }
    setComparing(true);
    try {
      const data = await apiPost('/api/forecast-versions/compare', { version_ids: selectedIds });
      setCompareResult(data);
    } catch (e) {
      console.error('Version compare failed:', e);
      setCompareResult(null);
    } finally {
      setComparing(false);
    }
  }, []);

  // ── Derived data ─────────────────────────────────────────────────────
  // Monthly summary — backend returns it inside each forecast item
  const monthlySummary =
    forecastItem?.monthly_summary ||
    dailyForecastData?.monthly_summary ||
    null;

  const forecastMetrics = (() => {
    if (!forecastItem) return [];
    const dq       = forecastItem.data_quality || {};
    const totalBase  = (forecastItem.base_forecast || []).reduce((a, b) => a + b, 0);
    const totalPromo = (forecastItem.promotional_forecast || []).reduce((a, b) => a + b, 0);
    const promoDays  = (forecastItem.promotional_forecast || []).filter(v => v > 0).length;
    return [
      { label: 'Total Forecast Demand', value: formatNum(totalBase + totalPromo), sub: `${forecastItem.dates?.length || 0} days`,          icon: '📦', color: 'blue',   tooltip: TOOLTIP_CONTENT.total_forecast },
      { label: 'Base Demand',           value: formatNum(totalBase),              sub: 'Non-promotional',                                   icon: '📈', color: 'teal',   tooltip: TOOLTIP_CONTENT.base_demand  },
      { label: 'Promotional Demand',    value: formatNum(totalPromo),             sub: promoDays > 0 ? `${promoDays} promo days` : 'None',  icon: '🏷️', color: 'purple', tooltip: TOOLTIP_CONTENT.promo_demand },
      {
        label: 'Historical Records',
        value: formatNum(dq.total_historical_records),
        sub:   forecastItem.data_source === 'hybrid_model_predictions' ? '✅ Model-backed' : '⚠️ Synthetic',
        icon:  '🗄️',
        color: forecastItem.data_source === 'hybrid_model_predictions' ? 'green' : 'red',
        tooltip: TOOLTIP_CONTENT.historical_records,
      },
    ];
  })();

  const impactStats = (() => {
    if (!forecastItem) return null;
    const dq  = forecastItem.data_quality || {};
    const pia = forecastItem.promotional_impact || {};
    return {
      liftPct:      pia.avg_promotional_lift != null ? (pia.avg_promotional_lift * 100).toFixed(1) : null,
      frequency:    pia.promotional_frequency,
      calendarUsed: pia.calendar_promos_used,
      promoDays:    (forecastItem.promotional_forecast || []).filter(v => v > 0).length,
      dateRange:    dq.date_range || {},
      promoRecords: dq.promotional_records || 0,
      method:       forecastItem.forecasting_method || '',
    };
  })();

  const intensityColor = (i) =>
    i === 'High' ? '#ef4444' : i === 'Medium' ? '#f59e0b' : '#22c55e';

  // ── Inline message renderer ──────────────────────────────────────────
  const InlineMsg = ({ msg }) => !msg ? null : (
    <div style={{
      width: '100%', padding: '6px 10px', borderRadius: '6px', fontSize: '0.8rem',
      background: msg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
      color:      msg.type === 'success' ? '#15803d'            : '#dc2626',
      border:     `1px solid ${msg.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
    }}>
      {msg.text}
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="dashboard-window">

      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-title-row">
          <div>
            <h2 className="dashboard-page-title">📊 Global Supply Chain Control Tower</h2>
            <p className="dashboard-page-sub">KPI Analytics &amp; Unified Demand Forecasting</p>
          </div>
          {forecastItem && (
            <div className={`data-source-badge ${forecastItem.data_source === 'hybrid_model_predictions' ? 'badge-real' : 'badge-synthetic'}`}
                 style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              {forecastItem.data_source === 'hybrid_model_predictions' ? '✅ Hybrid Model' : '⚠️ Synthetic Fallback'}
              <HelpTooltip {...TOOLTIP_CONTENT.data_source} />
            </div>
          )}
        </div>
      </div>

      {/* Enterprise KPIs - Executive only */}
      {canSeeKPIs && (
      <div className="global-kpi-section" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '12px', color: '#64748b',
          display: 'flex', alignItems: 'center', gap: '12px' }}>
          Enterprise KPIs
          <button
            id="btn-refresh-kpis"
            onClick={() => fetchKPIs(lastParams)}
            disabled={kpiLoading}
            title="Refresh KPI values"
            style={{
              fontSize: '0.75rem', padding: '3px 10px', borderRadius: '20px',
              border: '1px solid #cbd5e1', background: kpiLoading ? '#f1f5f9' : '#fff',
              cursor: kpiLoading ? 'not-allowed' : 'pointer', color: '#64748b',
            }}
          >
            {kpiLoading ? '⏳' : '🔄 Refresh'}
          </button>
        </h3>
      {kpiLoading ? (
        <div style={{ display: 'flex', gap: '1rem' }}>
          {[1,2,3,4,5].map(i => (
              <div key={i} style={{
                flex: 1, height: '90px', borderRadius: '12px',
                background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)',
                backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
              }} />
            ))}
          </div>
        ) : (
          <KPICards cards={globalKPIs} />
        )}
        {kpiDetails && (
          <div style={{
            marginTop: '14px',
            padding: '14px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(59,130,246,0.18)',
            background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(14,165,233,0.04))',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <div style={{ fontWeight: 800, color: '#1d4ed8' }}>Forecast Accuracy Details</div>
                <div style={{ fontSize: '0.82rem', color: '#64748b' }}>{kpiDetails.precision_basis || 'Holdout metrics'}</div>
              </div>
              <button
                type="button"
                onClick={() => setKpiDetails(null)}
                style={{
                  border: '1px solid rgba(59,130,246,0.25)',
                  background: '#fff',
                  color: '#1d4ed8',
                  borderRadius: '999px',
                  padding: '6px 12px',
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                Close
              </button>
            </div>
            <div style={{
              marginTop: '12px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '10px',
            }}>
              {[
                ['Accuracy', kpiDetails.accuracy != null ? `${kpiDetails.accuracy.toFixed(2)}%` : 'N/A'],
                ['Precision', kpiDetails.precision != null ? `${kpiDetails.precision.toFixed(2)}%` : 'N/A'],
                ['F1 Score', kpiDetails.f1_score != null ? `${kpiDetails.f1_score.toFixed(2)}%` : 'N/A'],
                ['Response Time', kpiDetails.response_time_ms != null ? `${kpiDetails.response_time_ms.toFixed(1)} ms` : 'N/A'],
                ['Response Score', kpiDetails.response_time_score != null ? `${kpiDetails.response_time_score.toFixed(1)}%` : 'N/A'],
              ].map(([label, value]) => (
                <div key={label} style={{
                  borderRadius: '10px',
                  padding: '10px 12px',
                  background: '#fff',
                  border: '1px solid rgba(59,130,246,0.14)',
                }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {label}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Daily Forecast Control */}
      <DailyForecastControl
        onDailyForecast={handleForecast}
        loading={loading}
        dataSummary={dataSummary}
      />

      {/* NPI Badge — shows when a specific SKU is selected */}
      {selectedSkuId && <NPIBadge skuId={selectedSkuId} />}

      {/* Save Forecast / Save Version toolbar */}
      {forecastItem && (
        <div className="save-forecast-bar" style={{
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
          marginBottom: '16px', padding: '10px 16px',
          background: 'rgba(59,130,246,0.06)', borderRadius: '10px',
          border: '1px solid rgba(59,130,246,0.15)',
        }}>

          {/* Save to forecast log */}
          <button
            id="btn-save-forecast"
            className="btn btn-primary"
            onClick={handleSaveForecast}
            disabled={saving || !lastParams}
            style={{ fontSize: '0.85rem', padding: '6px 14px' }}
          >
            {saving ? '⏳ Saving…' : '💾 Save Forecast'}
          </button>
          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
            Saves to forecast log for audit history and comparisons
          </span>

          {/* Save as named version */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
            <input
              id="input-version-name"
              type="text"
              placeholder="Version name…"
              value={versionName}
              onChange={e => setVersionName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSaveVersion()}
              style={{
                padding: '5px 10px', borderRadius: '6px',
                border: '1px solid #cbd5e1', fontSize: '0.82rem', width: '150px',
              }}
            />
            <button
              id="btn-save-version"
              className="btn btn-secondary"
              onClick={handleSaveVersion}
              disabled={savingVersion || !versionName.trim() || !forecastItem}
              style={{ fontSize: '0.85rem', padding: '6px 14px', whiteSpace: 'nowrap' }}
            >
              {savingVersion ? '⏳ Saving…' : '📌 Save Version'}
            </button>
            <button
              id="btn-toggle-versions"
              className="btn btn-ghost"
              onClick={() => setShowVersions(v => !v)}
              style={{ fontSize: '0.82rem', padding: '6px 12px', whiteSpace: 'nowrap' }}
            >
              {showVersions ? '▲ Hide Versions' : '▼ View Versions'}
            </button>
          </div>

          <InlineMsg msg={saveMsg} />
          <InlineMsg msg={versionMsg} />
        </div>
      )}

      {/* Forecast Metrics */}
      {canSeeKPIs && forecastItem && forecastMetrics.length > 0 && (
        <div className="forecast-metrics-section" style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: '12px', color: '#64748b' }}>
            Forecast Metrics
          </h3>
          <KPICards cards={forecastMetrics} />
        </div>
      )}

      {/* Monthly Summary — region-aware, only shown when backend populates it */}
      {monthlySummary && (
        <div className="monthly-summary-section" style={{
          marginBottom: '24px', padding: '16px 20px', borderRadius: '12px',
          background: 'linear-gradient(135deg,rgba(99,102,241,0.08),rgba(139,92,246,0.05))',
          border: '1px solid rgba(99,102,241,0.2)',
        }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '12px', color: '#6366f1',
            display: 'flex', alignItems: 'center', gap: '8px' }}>
            📅 Monthly Summary
            {monthlySummary.region && (
              <span style={{
                fontSize: '0.75rem', background: 'rgba(99,102,241,0.15)', borderRadius: '20px',
                padding: '2px 8px', color: '#818cf8',
              }}>
                {monthlySummary.region}
              </span>
            )}
          </h3>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>
                Total Monthly Demand
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' }}>
                {formatNum(monthlySummary.total_monthly_demand)}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>
                Growth Rate (Baseline)
              </div>
              <div style={{
                fontSize: '1.4rem', fontWeight: 700,
                color: monthlySummary.monthly_growth_rate >= 0 ? '#22c55e' : '#ef4444',
              }}>
                {monthlySummary.monthly_growth_rate >= 0 ? '+' : ''}{monthlySummary.monthly_growth_rate}%
              </div>
            </div>
            <div style={{ flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                Promotion Intensity
                <HelpTooltip {...TOOLTIP_CONTENT.intensity} />
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700,
                color: intensityColor(monthlySummary.promotion_intensity) }}>
                {monthlySummary.promotion_intensity || '—'}
              </div>
            </div>
            {monthlySummary.promo_to_base_ratio != null && (
              <div style={{ flex: 1, minWidth: '120px' }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  Promo / Base Ratio
                  <HelpTooltip {...TOOLTIP_CONTENT.promo_base_ratio} />
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f59e0b' }}>
                  {monthlySummary.promo_to_base_ratio}x
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Chart */}
      <div className="dashboard-chart-card">
        <div className="chart-card-header">
          <div>
            <h3 className="chart-card-title">📈 Demand Forecast Timeline</h3>
            {forecastItem && impactStats?.dateRange?.start && (
              <p className="chart-card-sub">
                Historical analysis: {impactStats.dateRange.start} → {impactStats.dateRange.end}
              </p>
            )}
          </div>
          {forecastItem && (
            <div className="chart-legend-pills" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span className="pill pill-blue">● Base</span>
              <span className="pill pill-purple">● Promo</span>
              <span className="pill pill-green">── Total</span>
              {showBaseline && (
                <span style={{
                  fontSize: '0.75rem', padding: '2px 8px', borderRadius: '20px',
                  background: 'rgba(255,107,53,0.12)', color: '#FF6B35',
                  border: '1px solid rgba(255,107,53,0.3)', fontWeight: 600,
                }}>
                  📏 SMA Baseline
                  {baselineInfo?.avg != null && (
                    <span style={{ fontWeight: 400, marginLeft: '4px' }}>
                      = {Math.round(baselineInfo.avg).toLocaleString()} units/day mean
                    </span>
                  )}
                </span>
              )}
              {/* Baseline toggle button */}
              <button
                id="btn-toggle-baseline"
                onClick={handleToggleBaseline}
                disabled={baselineLoading || !lastParams}
                title={!lastParams ? 'Generate a forecast first' : showBaseline ? 'Hide SMA baseline' : 'Show SMA baseline overlay'}
                style={{
                  fontSize: '0.75rem', padding: '3px 10px', borderRadius: '20px',
                  border: `1px solid ${showBaseline ? 'rgba(255,107,53,0.5)' : '#cbd5e1'}`,
                  background: showBaseline ? 'rgba(255,107,53,0.1)' : '#fff',
                  cursor: (!lastParams || baselineLoading) ? 'not-allowed' : 'pointer',
                  color: showBaseline ? '#FF6B35' : '#64748b',
                  fontWeight: showBaseline ? 600 : 400,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {baselineLoading ? '⏳' : showBaseline ? '📏 Hide Baseline' : '📏 Show Baseline'}
              </button>
            </div>
          )}
        </div>
        <div className="chart-card-body">
          {loading ? (
            <div className="chart-loading-state">
              <div className="loading-spinner" />
              <p>Generating forecast…</p>
            </div>
          ) : (
            <DailyForecastApexChart
              forecastData={forecastItem}
              baselineData={showBaseline ? baselineData : null}
            />
          )}
        </div>
      </div>

      {/* Promotional Impact */}
      {forecastItem && impactStats && (
        <div className="promo-impact-section">
          <div className="section-card-header">
            <h3>🏷️ Promotional Impact Analysis</h3>
            <span className="section-badge">
              {impactStats.calendarUsed ? '📅 Calendar-driven' : '📊 History-derived'}
            </span>
          </div>
          <div className="impact-grid">
            <div className="impact-item impact-blue">
              <span className="impact-icon">📊</span>
              <div className="impact-body">
                <div className="impact-value">{impactStats.liftPct ? `+${impactStats.liftPct}%` : '—'}</div>
                <div className="impact-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Avg Promotional Lift
                  <HelpTooltip {...TOOLTIP_CONTENT.avg_lift} />
                </div>
              </div>
            </div>
            <div className="impact-item impact-purple">
              <span className="impact-icon">📅</span>
              <div className="impact-body">
                <div className="impact-value">{impactStats.promoDays}</div>
                <div className="impact-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Promotional Days in Forecast
                  <HelpTooltip {...TOOLTIP_CONTENT.promo_demand} />
                </div>
              </div>
            </div>
            <div className="impact-item impact-green">
              <span className="impact-icon">📋</span>
              <div className="impact-body">
                <div className="impact-value">{pct(impactStats.frequency)}</div>
                <div className="impact-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Historical Promo Frequency
                  <HelpTooltip {...TOOLTIP_CONTENT.promo_frequency} />
                </div>
              </div>
            </div>
            <div className="impact-item impact-orange">
              <span className="impact-icon">🗃️</span>
              <div className="impact-body">
                <div className="impact-value">{formatNum(impactStats.promoRecords)}</div>
                <div className="impact-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Historical Promo Records
                  <HelpTooltip {...TOOLTIP_CONTENT.promo_records} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version Selector panel */}
      {showVersions && (
        <div style={{ marginTop: '24px' }}>
          <VersionSelector onSelectionChange={handleVersionCompare} />
          {comparing && (
            <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '8px' }}>⏳ Comparing versions…</p>
          )}
          {compareResult && compareResult.versions && compareResult.versions.length > 0 && (
            <div style={{
              marginTop: '16px', padding: '16px', borderRadius: '10px',
              background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
            }}>
              <h4 style={{ fontSize: '0.95rem', color: '#6366f1', marginBottom: '12px' }}>
                📊 Version Comparison
              </h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid rgba(99,102,241,0.2)' }}>
                    {['Version', 'Total Forecast', 'Avg / Day', 'Δ vs First', 'Δ %'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareResult.versions.map((v, i) => (
                    <tr key={v.version_id} style={{
                      borderBottom: '1px solid rgba(99,102,241,0.1)',
                      background: i === 0 ? 'rgba(99,102,241,0.05)' : 'transparent',
                    }}>
                      <td style={{ padding: '6px 10px', fontWeight: i === 0 ? 700 : 400 }}>{v.name}</td>
                      <td style={{ padding: '6px 10px' }}>{formatNum(v.total_forecast)}</td>
                      <td style={{ padding: '6px 10px' }}>{formatNum(v.avg_forecast)}</td>
                      <td style={{ padding: '6px 10px', color: v.delta_vs_first > 0 ? '#22c55e' : v.delta_vs_first < 0 ? '#ef4444' : '#64748b' }}>
                        {i === 0 ? '—' : (v.delta_vs_first > 0 ? '+' : '') + formatNum(v.delta_vs_first)}
                      </td>
                      <td style={{ padding: '6px 10px', color: v.delta_pct_vs_first > 0 ? '#22c55e' : v.delta_pct_vs_first < 0 ? '#ef4444' : '#64748b' }}>
                        {i === 0 ? '—' : `${v.delta_pct_vs_first > 0 ? '+' : ''}${v.delta_pct_vs_first.toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!forecastItem && !loading && (
        <div className="dashboard-empty-state">
          <div className="empty-icon">📊</div>
          <h3>Ready to forecast</h3>
          <p>Select your parameters above and click <strong>Generate Forecast</strong> to see results.</p>
          <div className="empty-features">
            <div className="empty-feature">🎯 Unified methodology — same engine as Promotional Analysis</div>
            <div className="empty-feature">📅 Promotional calendar integration with product matching</div>
            <div className="empty-feature">📈 Day-of-week patterns from non-promotional baseline</div>
            <div className="empty-feature">🔍 Region-aware monthly summaries based on the active dataset</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardWindow;
